import { COPY_POLICIES, TITLE_COPY_LIMITS } from "./copy-policy";
import { validateScalarSlotLength } from "./scalar-slot-validation";
export const TIMELINE_MILESTONE_MAX_LENGTHS = {
    titleTop: COPY_POLICIES.title.maxLength,
    titleBottom: COPY_POLICIES.title.maxLength,
    descriptionHeader: COPY_POLICIES.title.maxLength,
    description: COPY_POLICIES.description.maxLength,
};
/**
 * Legacy picture framing, retained so existing stored crops remain editable.
 * New gallery entries use the original image; the frontend crops only the cover
 * to 350x263 (300px wide on narrow screens) and contains it in the lightbox.
 */
export const TIMELINE_PICTURE_SLOT = {
    key: "image",
    label: "Image",
    targets: [{ width: 400, height: 300, aspectRatio: "4:3" }],
};
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Does this part say anything at all? */
function saysNothing(value) {
    return typeof value !== "string" || value.trim() === "";
}
/**
 * A stored picture reference, or nothing.
 *
 * A reference without a framing points at no image the delivery layer can
 * render, so it is "no picture" rather than a malformed one.
 */
function hasPicture(value) {
    return (isRecord(value) && typeof value.cropId === "string" && value.cropId !== "");
}
/** The plain-words parts of a milestone, in the order an Editor composes them. */
const MILESTONE_PARTS = [
    {
        key: "titleTop",
        label: "Title top",
        reason: "Every milestone needs a title top",
        maxLength: TIMELINE_MILESTONE_MAX_LENGTHS.titleTop,
    },
    {
        key: "titleBottom",
        label: "Title bottom",
        reason: "Every milestone needs a title bottom",
        maxLength: TIMELINE_MILESTONE_MAX_LENGTHS.titleBottom,
    },
    {
        key: "descriptionHeader",
        label: "Description header",
        reason: "Every milestone needs a description header",
        maxLength: TIMELINE_MILESTONE_MAX_LENGTHS.descriptionHeader,
    },
    {
        key: "description",
        label: "Description",
        reason: "Every milestone needs a description",
        maxLength: TIMELINE_MILESTONE_MAX_LENGTHS.description,
    },
];
/** Validate a whole Timeline. */
export function validateTimeline(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Timeline is malformed" };
    }
    const { eyebrow, milestones } = value;
    if (saysNothing(eyebrow)) {
        return { valid: false, reason: "A timeline needs an eyebrow" };
    }
    if (typeof value.timelineTitle === "string") {
        const timelineTitleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Timeline title" }, value.timelineTitle);
        if (!timelineTitleLength.valid)
            return timelineTitleLength;
    }
    if (!Array.isArray(milestones)) {
        return { valid: false, reason: "The Timeline is malformed" };
    }
    if (milestones.length === 0) {
        return { valid: false, reason: "A timeline needs at least one milestone" };
    }
    // Years are compared TRIMMED. Two years differing only in spacing are distinct
    // strings, so a renderer keyed on them would survive — but they read
    // identically to a visitor and give two controls the same accessible name,
    // which is the harm this rule exists to prevent. The STORED year is still
    // exactly what the Editor entered.
    const yearsSeen = new Set();
    for (const milestone of milestones) {
        if (!isRecord(milestone)) {
            return { valid: false, reason: "The Timeline is malformed" };
        }
        if (saysNothing(milestone.year)) {
            return { valid: false, reason: "Every milestone needs a year" };
        }
        const year = milestone.year.trim();
        if (yearsSeen.has(year)) {
            return { valid: false, reason: "Two milestones cannot share a year" };
        }
        yearsSeen.add(year);
        if (!hasPicture(milestone.image)) {
            return { valid: false, reason: "Every milestone needs an image" };
        }
        for (const part of MILESTONE_PARTS) {
            const field = milestone[part.key];
            if (saysNothing(field)) {
                return { valid: false, reason: part.reason };
            }
            const length = validateScalarSlotLength(part, field);
            if (!length.valid)
                return length;
        }
    }
    return {
        valid: true,
        timeline: value,
    };
}
export const timelineRowId = (row) => row.id ?? row.itemId;
/** Old copy/image slots belong to an implicit first group and are never overwritten. */
export function hasLegacyTimelineGroup(row, gallery = []) {
    return (Boolean(row.media?.image) ||
        ["descriptionHeader", "description"].some((key) => Boolean(row.scalars?.[key]?.trim())) ||
        gallery.some((image) => image.scalars?.groupId === timelineRowId(row) &&
            Boolean(timelineRowId(row))));
}
export function timelineGroupsFromBlock(block) {
    const repeats = block.repeats ?? {};
    const direction = block.scalars?.sortDirection === "desc" ? -1 : 1;
    const years = [...(repeats.milestones ?? [])].sort((a, b) => {
        const left = a.scalars?.year?.trim() ?? "";
        const right = b.scalars?.year?.trim() ?? "";
        if (!left || !right)
            return left ? -1 : right ? 1 : 0;
        return direction * left.localeCompare(right, undefined, { numeric: true });
    });
    return years.flatMap((year, index) => {
        const yearId = timelineRowId(year);
        const children = (repeats.groups ?? []).filter((group) => yearId && group.scalars?.yearId === yearId);
        // An empty new year retains its place in the preview while being composed.
        const groups = hasLegacyTimelineGroup(year, repeats.gallery) || !children.length
            ? [year, ...children]
            : children;
        return groups.map((group, groupIndex) => {
            const id = timelineRowId(group) ?? `year-${index}-group-${groupIndex}`;
            const images = [];
            if (group === year && year.media?.image)
                images.push({
                    media: year.media.image,
                    thumbnail: year.media.thumbnail,
                    repeatKey: "milestones",
                    itemId: timelineRowId(year),
                    alt: year.scalars?.descriptionHeader ?? "",
                });
            for (const picture of repeats.gallery ?? []) {
                if (picture.scalars?.groupId === id && picture.media?.image) {
                    images.push({
                        media: picture.media.image,
                        thumbnail: picture.media.thumbnail,
                        repeatKey: "gallery",
                        itemId: timelineRowId(picture),
                        alt: picture.scalars?.alt ?? "",
                    });
                }
            }
            return {
                id,
                year: year.scalars?.year ?? "",
                title: group.scalars?.descriptionHeader ?? "",
                description: group.scalars?.description ?? "",
                images,
            };
        });
    });
}
/** Draft rows may be incomplete during editing; publication requires complete groups. */
export function collectTimelinePublishIssues(block) {
    const issues = [];
    const repeats = block.repeats ?? {};
    const years = repeats.milestones ?? [];
    const add = (message, repeatKey, row, fieldKey, fieldKind = "scalar") => {
        issues.push({
            message,
            repeatKey,
            itemId: row && timelineRowId(row),
            fieldKey,
            fieldKind,
        });
    };
    if (!years.length)
        add("Timeline needs at least one year.", "milestones", undefined, "milestones", "group");
    const seen = new Set();
    for (const row of years) {
        const year = row.scalars?.year?.trim();
        if (!year)
            add("Every timeline year needs a year.", "milestones", row, "year");
        else if (seen.has(year))
            add("Timeline years must be unique.", "milestones", row, "year");
        if (year)
            seen.add(year);
    }
    const yearIds = new Set(years.map(timelineRowId).filter(Boolean));
    for (const group of repeats.groups ?? []) {
        if (!yearIds.has(group.scalars?.yearId))
            add("Every milestone group must belong to a year.", "groups", group, "yearId");
    }
    const groups = timelineGroupsFromBlock(block);
    const groupIds = new Set(groups.map((group) => group.id));
    for (const picture of repeats.gallery ?? []) {
        if (!groupIds.has(picture.scalars?.groupId ?? ""))
            add("Every gallery image must belong to a milestone group.", "gallery", picture, "groupId");
        if (!picture.media?.image?.cropId)
            add("Choose an image for every gallery entry, or remove the empty entry.", "gallery", picture, "image", "media");
    }
    for (const group of groups) {
        if (!group.title.trim() &&
            !group.description.trim() &&
            !group.images.some((image) => image.media.cropId)) {
            const row = (repeats.groups ?? []).find((row) => timelineRowId(row) === group.id);
            const legacy = years.find((row) => timelineRowId(row) === group.id);
            add(`Every milestone group in ${group.year} needs at least one image, title, or description.`, row ? "groups" : "milestones", row ?? legacy, "descriptionHeader");
        }
    }
    return issues;
}
export function validateTimelineBlock(block) {
    const [issue] = collectTimelinePublishIssues(block);
    return issue ? { valid: false, reason: issue.message } : { valid: true };
}
//# sourceMappingURL=timeline.js.map